import { createContext, useContext, useEffect, type ReactNode, type Ref, type UIEventHandler } from 'react';

import { createStore } from '../core/store.ts';
import { useStrings } from '../i18n/index.ts';
import { popRoute } from '../state/router.ts';
import type { IconName } from './icons.tsx';
import { cx, GlassButton } from './kit.tsx';

/**
 * Положение экрана в стеке: корневой, верхний или закрытый другим. `onBack` задаёт тот, кто
 * держит экран не в стеке вкладки, а у себя (лист со своими шагами). `entryKey` — запись стека вкладки.
 */
export type ScreenPlace = { root: boolean; covered: boolean; onBack?: () => void; entryKey?: number };

export const ScreenPlaceContext = createContext<ScreenPlace>({ root: true, covered: false });

/** Записи стека, экраны которых держат свою нижнюю панель: пока такая запись наверху, вкладки и корзина уходят. */
export const noDockEntries = createStore<ReadonlySet<number>>(new Set());

export type NavAction = { icon: IconName; label: string; onClick: () => void; tone?: 'danger' | 'accent' };

/**
 * Экран стека: прокручиваемый слой со своей полосой навигации. У корня вкладки полосы нет;
 * у вложенного экрана слева круглая кнопка «назад», справа действия.
 */
export function Screen(props: {
  children: ReactNode;
  actions?: NavAction[];
  /** Закреплённое под полосой навигации: чипы категории. */
  pinned?: ReactNode;
  /** Экран со своей нижней панелью: под вкладки место не оставляется. */
  noDock?: boolean;
  /** Полоса навигации лежит поверх содержимого, а не над ним: фото товара под ней. */
  overlayNav?: boolean;
  /** Экран внутри листа: занимает его тело, а не всё окно. */
  embedded?: boolean;
  /** Слева вместо «назад» крестик: первый шаг листа. */
  closeIcon?: boolean;
  className?: string;
  scrollRef?: Ref<HTMLDivElement>;
  onScroll?: UIEventHandler<HTMLDivElement>;
}) {
  const strings = useStrings();
  const place = useContext(ScreenPlaceContext);
  const showsBack = !place.root || place.onBack !== undefined;
  const showsNav = showsBack || (props.actions?.length ?? 0) > 0;

  const hidesDock = props.noDock === true && props.embedded !== true;
  const entryKey = place.entryKey;
  useEffect(() => {
    if (!hidesDock || entryKey === undefined) return;
    noDockEntries.set((prev) => new Set(prev).add(entryKey));
    return () =>
      noDockEntries.set((prev) => {
        const next = new Set(prev);
        next.delete(entryKey);
        return next;
      });
  }, [hidesDock, entryKey]);

  return (
    <div
      ref={props.scrollRef}
      className={cx(
        'screen',
        place.covered && 'is-covered',
        place.root ? 'is-tab-root' : 'is-pushed',
        (props.noDock || props.embedded) && 'no-dock',
        props.embedded && 'is-embedded',
        props.className,
      )}
      // Закрытый экран остаётся в дереве ради прокрутки и состояния, но не ловит фокус.
      inert={place.covered}
      onScroll={props.onScroll}
    >
      {showsNav && (
        <div className="navbar" style={props.overlayNav ? { marginBottom: -52 } : undefined}>
          {showsBack ? (
            <GlassButton icon={props.closeIcon ? 'close' : 'chevronLeft'} label={strings.close} onClick={place.onBack ?? popRoute} />
          ) : (
            <span />
          )}
          <div className="navbar-actions">
            {props.actions?.map((action) => (
              <GlassButton key={action.label} icon={action.icon} label={action.label} onClick={action.onClick} {...(action.tone ? { tone: action.tone } : {})} />
            ))}
          </div>
        </div>
      )}
      {props.pinned}
      {props.children}
    </div>
  );
}
